import { createMemo, For, Show } from "solid-js";
import { simulation } from "../domaine/force";
import { aretesDePlacement } from "../domaine/graphe";
import type { Similarites } from "../domaine/similarite";
import type { Portefeuille } from "../domaine/types";
import { couleurClasse, ongletsPour, type IdVue } from "./analyse";
import { secteur } from "./GrapheSimilarite";
import { Icone } from "./Icone";

/** Les vues de la donnée, dites en une ligne : de quoi choisir par où entrer. */
const RESUMES: Partial<Record<IdVue, string>> = {
  graphe: "Les projets qui se ressemblent, reliés au-delà d'un seuil.",
  arbres: "Culture › bioagresseur et filière › plan d'action.",
  leviers: "Les familles de leviers mobilisées par chaque projet.",
  autres: "Similarité, croisements, couverture, chronologie.",
};
const ENTREES = ongletsPour("accueil").filter((v) => !v.aDroite);

/** Ce que dit chaque compteur, en une phrase. */
const EXPLICATIONS: Record<string, string> = {
  projets: "dans le portefeuille affiché",
  rapprochements: "paires assez proches pour être reliées",
  groupes: "familles de projets reliés entre eux",
  "sans équivalent": "projets sans voisin au seuil",
};

const PREMIERS_PAS = [
  { icone: "loupe", texte: "Cherchez un projet, une culture ou un porteur dans la barre." },
  {
    icone: "filtre",
    texte: "Filtrez par bioagresseur, levier, filière : toutes les vues suivent.",
  },
  { icone: "lien", texte: "Partagez la vue telle quelle : filtres et projet ouvert compris." },
] as const;

const LARGEUR_APERCU = 420;
const HAUTEUR_APERCU = 300;
const RAYON_APERCU = 6;

/**
 * Page d'accueil : l'avertissement « prototype » en tête, puis le but du site et
 * un aperçu du vrai réseau, les chiffres du portefeuille, les portes d'entrée
 * vers les vues et trois gestes pour commencer. Les anomalies de la source ne
 * s'affichent pas : elles sont suivies en interne (tests/donnees-poc.test.ts).
 */
export function VueAccueil(props: {
  chiffres: { intitule: string; valeur: number }[];
  portefeuille: Portefeuille;
  similarites: Similarites;
  allerA: (vue: IdVue) => void;
}) {
  // Le placement par plus proches voisins, le même que le réseau par défaut, en
  // plus petit : un aperçu fidèle, pas une illustration.
  const apercu = createMemo(() => {
    const n = props.portefeuille.projets.length;
    if (n < 2) return null;
    const sim = simulation(
      n,
      aretesDePlacement(props.similarites).map((e) => ({
        source: e.a,
        cible: e.b,
        poids: e.score,
      })),
      {
        largeur: LARGEUR_APERCU,
        hauteur: HAUTEUR_APERCU,
        rayonX: RAYON_APERCU + 4,
        rayonY: RAYON_APERCU + 4,
      },
    );
    sim.stabiliser();
    const b = sim.boite();
    // Le seul voisin le plus proche de chaque projet, en trait léger : assez pour
    // montrer un réseau, pas assez pour en faire une pelote à cette taille.
    const liens = aretesDePlacement(props.similarites, 1);
    const marge = 18;
    const k = Math.min(
      (LARGEUR_APERCU - 2 * marge) / Math.max(b.largeur, 1),
      (HAUTEUR_APERCU - 2 * marge) / Math.max(b.hauteur, 1),
    );
    const points = sim.noeuds().map((p) => ({
      x: LARGEUR_APERCU / 2 + (p.x - (b.x0 + b.largeur / 2)) * k,
      y: HAUTEUR_APERCU / 2 + (p.y - (b.y0 + b.hauteur / 2)) * k,
    }));
    return { points, liens };
  });

  return (
    <div class="accueil">
      <p class="avertissement-prototype" role="note">
        <strong>Prototype</strong>
        <span>
          Version de travail, non officielle : contenus et calculs peuvent changer.{" "}
          <button type="button" class="lien-projet" onClick={() => props.allerA("avis")}>
            Donner votre avis
          </button>
        </span>
      </p>
      <section class="accueil-ouverture">
        <div class="accueil-intro">
          <p class="accueil-surtitre">Programme PARSADA</p>
          <h2>Ce qui rapproche les projets PARSADA</h2>
          <p>
            ParsadaMap compare les projets du programme selon leurs familles de leviers, leurs
            cultures et les bioagresseurs visés, pour repérer les projets proches et ceux qui
            restent isolés.
          </p>
          <div class="accueil-actions">
            <button
              type="button"
              class="bouton bouton-principal"
              onClick={() => props.allerA("graphe")}
            >
              Explorer le réseau
              <span class="accueil-fleche" aria-hidden="true">
                →
              </span>
            </button>
            <button type="button" class="bouton" onClick={() => props.allerA("arbres")}>
              Parcourir par culture
            </button>
          </div>
        </div>

        <Show when={apercu()}>
          {(a) => (
            <button
              type="button"
              class="accueil-apercu"
              aria-label="Aperçu du réseau : ouvrir le graphe"
              onClick={() => props.allerA("graphe")}
            >
              <svg
                viewBox={`0 0 ${LARGEUR_APERCU} ${HAUTEUR_APERCU}`}
                aria-hidden="true"
                class="accueil-apercu-dessin"
              >
                <g class="accueil-apercu-liens">
                  <For each={a().liens}>
                    {(e) => {
                      const p = a().points[e.a];
                      const q = a().points[e.b];
                      return (
                        <Show when={p && q}>
                          <line x1={p?.x} y1={p?.y} x2={q?.x} y2={q?.y} />
                        </Show>
                      );
                    }}
                  </For>
                </g>
                <For each={props.portefeuille.projets}>
                  {(projet, i) => {
                    const pt = () => a().points[i()];
                    const cibles = () => projet.bioagresseurs;
                    return (
                      <g
                        class="accueil-apercu-noeud"
                        style={{ "--retard": `${(i() % 15) * 40}ms` }}
                        transform={`translate(${pt()?.x.toFixed(1) ?? 0},${pt()?.y.toFixed(1) ?? 0})`}
                      >
                        <Show
                          when={cibles().length > 1}
                          fallback={
                            <circle
                              r={RAYON_APERCU}
                              fill={couleurClasse(props.portefeuille, cibles()[0] ?? "")}
                            />
                          }
                        >
                          <For each={cibles()}>
                            {(c, k) => {
                              const pas = () => (Math.PI * 2) / cibles().length;
                              return (
                                <path
                                  d={secteur(
                                    RAYON_APERCU,
                                    -Math.PI / 2 + k() * pas(),
                                    -Math.PI / 2 + (k() + 1) * pas(),
                                  )}
                                  fill={couleurClasse(props.portefeuille, c)}
                                />
                              );
                            }}
                          </For>
                        </Show>
                      </g>
                    );
                  }}
                </For>
              </svg>
              <span class="accueil-apercu-legende">
                Chaque point est un projet, coloré par bioagresseur visé{" "}
                <span aria-hidden="true">· ouvrir →</span>
              </span>
            </button>
          )}
        </Show>
      </section>

      <ul class="accueil-chiffres" aria-label="Le portefeuille en chiffres">
        <For each={props.chiffres}>
          {(c) => (
            <li class="accueil-chiffre">
              <span class="valeur">{c.valeur.toLocaleString("fr-FR")}</span>
              <span class="intitule">{c.intitule}</span>
              <span class="accueil-chiffre-detail">{EXPLICATIONS[c.intitule]}</span>
            </li>
          )}
        </For>
      </ul>

      <section aria-labelledby="accueil-vues">
        <h3 id="accueil-vues" class="accueil-titre-section">
          Par où entrer
        </h3>
        <nav class="accueil-entrees" aria-label="Vues">
          <For each={ENTREES}>
            {(e) => (
              <button type="button" class="accueil-entree" onClick={() => props.allerA(e.id)}>
                <span class="accueil-entree-icone" aria-hidden="true">
                  <Icone nom={e.icone} />
                </span>
                <span class="accueil-entree-texte">
                  <span class="accueil-entree-titre">{e.libelle}</span>
                  <span class="accueil-entree-resume">{RESUMES[e.id]}</span>
                </span>
                <span class="accueil-fleche" aria-hidden="true">
                  →
                </span>
              </button>
            )}
          </For>
        </nav>
      </section>

      <section aria-labelledby="accueil-pas">
        <h3 id="accueil-pas" class="accueil-titre-section">
          Pour commencer
        </h3>
        <ol class="accueil-pas">
          <For each={PREMIERS_PAS}>
            {(p) => (
              <li>
                <span class="accueil-pas-icone" aria-hidden="true">
                  <Icone nom={p.icone} />
                </span>
                {p.texte}
              </li>
            )}
          </For>
        </ol>
      </section>
    </div>
  );
}
